import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { I18nProvider } from "./context/I18nContext";
import { CityProvider } from "./context/CityContext";
import AuthCallback from "./components/AuthCallback";
import { useAuth } from "./context/AuthContext";
import PlatformHome from "./pages/customer/PlatformHome";
import RestaurantPage from "./pages/customer/RestaurantPage";
import OrderTracking from "./pages/customer/OrderTracking";
import AdminPanel from "./pages/admin/AdminPanel";
import B2BApplications from "./pages/admin/B2BApplications";
import B2BLanding from "./pages/b2b/B2BLanding";
import { RestaurantLogin, RestaurantEntry, RestaurantLayout, MenuPage, OptionsPage, ManagerOnly } from "./pages/restaurant/RestaurantShell";
import OrdersBoard from "./pages/restaurant/OrdersBoard";
import InternalOrder from "./pages/restaurant/InternalOrder";
import AvailabilityPage from "./pages/restaurant/AvailabilityPage";
import OrderHistory from "./pages/restaurant/OrderHistory";
import HappyHourPage from "./pages/restaurant/HappyHourPage";
import LoyaltyPage from "./pages/restaurant/LoyaltyPage";
import AnalyticsPage from "./pages/restaurant/AnalyticsPage";
import ShiftsPage from "./pages/restaurant/ShiftsPage";
import RestaurantSettings from "./pages/restaurant/RestaurantSettings";
import TeamPage from "./pages/restaurant/TeamPage";
import InviteAccept from "./pages/restaurant/InviteAccept";
import { PrivacyPage, TermsPage, ContactPage } from "./pages/InfoPages";
import NotFound from "./pages/NotFound";
import PartnerLogin from "./pages/restaurant/PartnerLogin";
import AdminLogin from "./pages/admin/AdminLogin";
import { CTX, isCustomerCtx, isPartnerCtx, isAdminCtx } from "./lib/appContext";
import LegalTerms from "./pages/LegalTerms";
import LegalPrivacy from "./pages/LegalPrivacy";
import LegalTermsEn from "./pages/LegalTermsEn";
import LegalPrivacyEn from "./pages/LegalPrivacyEn";
import { BagLogo } from "./components/Logo";
import PwaInstallPrompt from "./components/PwaInstallPrompt";
import { CityOnboarding } from "./components/CityOnboarding";
import { initAnalytics, track } from "./lib/analytics";
import { initTechTelemetry } from "./lib/tech";
import "./lib/pwaInstall";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="animate-pop"><BagLogo size={72} /></div>
    </div>
  );
}

function RequireRole({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to={CTX === "admin" ? "/admin/login" : "/"} replace />;
  if (roles && !roles.includes(user.role)) {
    if (roles.includes("admin") && CTX === "admin") return <Navigate to="/admin/login" replace />;
    if (roles.includes("admin") && user.memberships?.length) return <Navigate to="/restaurant" replace />;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-center p-6" data-testid="admin-access-denied">
        <BagLogo size={56} />
        <h1 className="font-display font-black text-2xl">Nemate pristup</h1>
        <p className="text-muted-foreground">Ova stranica je dostupna samo administratoru.</p>
        <a href="/" className="text-brand-red font-semibold underline">Nazad na Ponesi</a>
      </div>
    );
  }
  return children;
}

// Panel sub-paths that must NEVER resolve on the customer host. They are reserved, so a customer
// restaurant page can never occupy them either.
const RESERVED_PANEL_SLUGS = ["login", "orders", "new-order", "vista", "availability", "history",
  "menu", "happy-hour", "analytics", "shifts", "options", "settings", "team"];

function AppRouter() {
  const location = useLocation();
  // One page_view per path per session (dedupe_key) — refreshes and rerenders never double-count.
  useEffect(() => {
    track(/^\/b2b/.test(location.pathname) ? "b2b_page_view" : "page_view",
          { dedupe_key: location.pathname });
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  const customerArea = !/^\/(restaurant|admin|team|b2b)(\/|$)/.test(location.pathname)
    || (/^\/restaurant\/[^/]+$/.test(location.pathname) && location.pathname !== "/restaurant/login");
  return (
    <>
    {customerArea && CTX !== "admin" && <CityOnboarding />}    <Routes>
      {/* ---- CUSTOMER context: mounted only on ponesi.rs / www.ponesi.rs ---- */}
      {isCustomerCtx && <>
      <Route path="/" element={<PlatformHome />} />
      <Route path="/restaurants" element={<PlatformHome />} />
      <Route path="/order/:id" element={<OrderTracking />} />
      {/* English customer routes (/en). Restaurant & admin panels stay Serbian-only. */}
      <Route path="/en" element={<PlatformHome />} />
      <Route path="/en/restaurants" element={<PlatformHome />} />
      <Route path="/en/restaurant/:slug" element={<RestaurantPage />} />
      <Route path="/en/order/:id" element={<OrderTracking />} />
      <Route path="/privatnost" element={<PrivacyPage />} />
      <Route path="/uslovi" element={<TermsPage />} />
      <Route path="/uslovi-koriscenja" element={<LegalTerms />} />
      <Route path="/politika-privatnosti" element={<LegalPrivacy />} />
      <Route path="/en/terms" element={<LegalTermsEn />} />
      <Route path="/en/privacy" element={<LegalPrivacyEn />} />
      <Route path="/kontakt" element={<ContactPage />} />
      <Route path="/b2b" element={<B2BLanding />} />
      <Route path="/en/kontakt" element={<ContactPage />} />
      <Route path="/team/invite/:token" element={<InviteAccept />} />
      </>}
      {/* Privileged page routes do not exist on the customer host: terminal 404, never a redirect,
          and no authorization state is revealed. Reserved slugs are matched BEFORE the customer
          restaurant page so /restaurant/orders can never render anything. */}
      {CTX === "customer" && <>
      <Route path="/restaurant" element={<NotFound />} />
      {RESERVED_PANEL_SLUGS.map((s) => <Route key={s} path={`/restaurant/${s}`} element={<NotFound />} />)}
      <Route path="/restaurant/:slug/*" element={<NotFound />} />
      <Route path="/admin" element={<NotFound />} />
      <Route path="/admin/*" element={<NotFound />} />
      </>}
      {isCustomerCtx && <Route path="/restaurant/:slug" element={<RestaurantPage />} />}

      {/* ---- RESTAURANT PARTNER context: mounted only on partner.ponesi.rs ---- */}
      {isPartnerCtx && <>
      <Route path="/restaurant" element={<RestaurantEntry />} />
      <Route path="/restaurant/login" element={<PartnerLogin />} />
      <Route element={<RestaurantLayout />}>
        <Route path="/restaurant/:rid/orders" element={<OrdersBoard />} />
        <Route path="/restaurant/:rid/new-order" element={<InternalOrder />} />
        <Route path="/restaurant/:rid/availability" element={<AvailabilityPage />} />
        <Route path="/restaurant/:rid/history" element={<OrderHistory />} />
        <Route path="/restaurant/:rid/menu" element={<ManagerOnly><MenuPage /></ManagerOnly>} />
        <Route path="/restaurant/:rid/happy-hour" element={<ManagerOnly><HappyHourPage /></ManagerOnly>} />
        <Route path="/restaurant/:rid/loyalty" element={<ManagerOnly><LoyaltyPage /></ManagerOnly>} />
        <Route path="/restaurant/:rid/analytics" element={<ManagerOnly><AnalyticsPage /></ManagerOnly>} />
        <Route path="/restaurant/:rid/shifts" element={<ManagerOnly><ShiftsPage /></ManagerOnly>} />
        <Route path="/restaurant/:rid/options" element={<ManagerOnly><OptionsPage /></ManagerOnly>} />
        <Route path="/restaurant/:rid/settings" element={<ManagerOnly><RestaurantSettings /></ManagerOnly>} />
        <Route path="/restaurant/:rid/team" element={<ManagerOnly perm="team"><TeamPage /></ManagerOnly>} />
      </Route>
      {CTX === "partner" && <Route path="/team/invite/:token" element={<InviteAccept />} />}
      </>}

      {/* ---- PLATFORM ADMIN context: mounted only on admin.ponesi.rs ---- */}
      {isAdminCtx && <>
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<RequireRole roles={["admin"]}><AdminPanel /></RequireRole>} />
      <Route path="/admin/b2b" element={<RequireRole roles={["admin"]}><B2BApplications /></RequireRole>} />
      </>}

      {/* Each host lands on its own entry; unknown paths never leak another context. */}
      {CTX === "partner" && <Route path="*" element={<Navigate to="/restaurant" replace />} />}
      {CTX === "admin" && <Route path="*" element={<Navigate to="/admin" replace />} />}
      {(CTX === "customer" || CTX === "all") && <Route path="*" element={<Navigate to="/" replace />} />}
    </Routes>
    <PwaInstallPrompt />
    </>
  );
}

// Per-host document identity: the customer PWA manifest must never be served to the Partner app
// (and Admin gets no manifest at all, so it stays a plain secure web console).
function applyHostShell() {
  const link = document.querySelector('link[rel="manifest"]');
  if (CTX === "admin") {
    link?.remove();
    document.title = "Ponesi Admin";
  } else if (CTX === "partner") {
    if (link) link.setAttribute("href", "/partner-manifest.json?v=3");
    document.title = "Ponesi Partner";
  } else {
    if (link) link.setAttribute("href", "/manifest.json?v=9");
    document.title = "Ponesi — Poruči. Ponesi. Uživaj.";
  }
}

function App() {
  useEffect(() => { applyHostShell(); initAnalytics(); initTechTelemetry(); }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <I18nProvider>
          <CityProvider>
            <AppRouter />
          </CityProvider>
        </I18nProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
